import { useDynamicDefaults } from './useDynamicDefaults';

interface FormElement {
  id: string;
  type: string;
  props: {
    default_value?: string | string[];
    choices?: Array<{
      id: string;
      content: string;
    }>;
  };
}

interface FormSettings {
  parameters?: Array<{
    name: string;
    description?: string;
  }>;
}

interface RouteParams {
  [key: string]: string;
}

type DefaultValue = string | string[] | boolean | undefined;

export function useFormDefaults(routeParams: RouteParams = {}) {
  const { resolveDynamicValue, isDynamicValue } = useDynamicDefaults(routeParams);

  const convertChoiceContentToId = (
    content: string,
    choices: Array<{ id: string; content: string }>
  ) => {
    const choice = choices.find((c) => c.content === content);
    return choice?.id || content;
  };

  const getDefaultValue = (
    element: FormElement,
    formData: Record<string, any>,
    formSettings?: FormSettings
  ): DefaultValue => {
    // Marketing opt-in must default to an explicit `false` (not undefined) so an
    // untouched checkbox is submitted as a deliberate "no". The backend keys on
    // this tri-state: absent = not asked, false = declined, true = opted in.
    if (element.type === 'marketing-opt-in') {
      return formData[element.id] === true || formData[element.id] === 'true';
    }

    // First check if we have a dynamic value in props that needs resolving
    if (element.props?.default_value && isDynamicValue(element.props.default_value)) {
      const resolvedDefaultValue = resolveDynamicValue(
        typeof element.props.default_value === 'string'
          ? element.props.default_value
          : element.props.default_value[0],
        formSettings || { parameters: [] }
      );

      // If we have a form data value and it's not a template, use that instead
      if (formData[element.id] !== undefined && !isDynamicValue(formData[element.id])) {
        return formData[element.id];
      }

      // For choice-based elements, convert content to ID(s)
      if (
        element.props.choices &&
        ['single-choice', 'multiple-choice', 'picture-choice'].includes(element.type)
      ) {
        if (element.type === 'multiple-choice') {
          // Handle comma-separated values for multiple choice
          const values =
            typeof resolvedDefaultValue === 'string'
              ? resolvedDefaultValue.split(',').map((v) => v.trim())
              : [];
          return values.map((value) => convertChoiceContentToId(value, element.props.choices!));
        }
        // For single-choice and picture-choice, ensure we have a string
        const stringValue =
          typeof resolvedDefaultValue === 'string'
            ? resolvedDefaultValue
            : String(resolvedDefaultValue);
        return convertChoiceContentToId(stringValue, element.props.choices);
      }

      return resolvedDefaultValue;
    }

    // If there's no dynamic template, use form data if available
    if (formData[element.id] !== undefined && formData[element.id] !== '') {
      return formData[element.id];
    }

    // If no form data and no dynamic template, use static default value
    if (element.props?.default_value) {
      // For choice-based elements with static default values
      if (
        element.props.choices &&
        ['single-choice', 'multiple-choice', 'picture-choice'].includes(element.type)
      ) {
        if (element.type === 'multiple-choice') {
          if (Array.isArray(element.props.default_value)) {
            return element.props.default_value.map((value) =>
              convertChoiceContentToId(value, element.props.choices!)
            );
          }
          // Handle comma-separated string values
          const values = (element.props.default_value as string).split(',').map((v) => v.trim());
          return values.map((value) => convertChoiceContentToId(value, element.props.choices!));
        }
        return convertChoiceContentToId(
          element.props.default_value as string,
          element.props.choices
        );
      }
      return element.props.default_value;
    }

    return undefined;
  };

  return {
    getDefaultValue,
  };
}
